import { useState, useRef, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/use-toast'

export default function Login() {
  const navigate = useNavigate()
  const { signIn } = useAuth()
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    const particles: Array<{x: number, y: number, size: number, speedX: number, speedY: number, opacity: number}> = []
    const particleCount = window.innerWidth < 768 ? 15 : 25

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 3 + 1,
        speedX: (Math.random() * 1.5 - 0.75) * (window.innerWidth < 768 ? 0.3 : 1),
        speedY: (Math.random() * 1.5 - 0.75) * (window.innerWidth < 768 ? 0.3 : 1),
        opacity: Math.random() * 0.3 + 0.1
      })
    }

    const drawXrayGrid = () => {
      ctx.strokeStyle = 'rgba(44, 62, 80, 0.1)'
      ctx.lineWidth = 0.5
      
      for (let x = 0; x < canvas.width; x += 50) {
        ctx.beginPath()
        ctx.moveTo(x, 0)
        ctx.lineTo(x, canvas.height)
        ctx.stroke()
      }
      
      for (let y = 0; y < canvas.height; y += 50) {
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(canvas.width, y)
        ctx.stroke()
      }

      ctx.strokeStyle = 'rgba(44, 62, 80, 0.05)'
      ctx.lineWidth = 0.2
      
      for (let x = 0; x < canvas.width; x += 10) {
        ctx.beginPath()
        ctx.moveTo(x, 0)
        ctx.lineTo(x, canvas.height)
        ctx.stroke()
      }
      
      for (let y = 0; y < canvas.height; y += 10) {
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(canvas.width, y)
        ctx.stroke()
      }
    }

    let scanLinePos = 0
    const drawScanLine = () => {
      ctx.fillStyle = 'rgba(44, 62, 80, 0.1)'
      ctx.fillRect(0, scanLinePos, canvas.width, 2)
      scanLinePos = (scanLinePos + (window.innerWidth < 768 ? 0.7 : 2)) % canvas.height
    }

    const drawMarkers = () => {
      ctx.fillStyle = 'rgba(44, 62, 80, 0.3)'
      ctx.font = '10px monospace'
      
      for (let y = 0; y < canvas.height; y += 50) {
        ctx.fillText(`${y}`, 5, y + 10)
      }
      
      for (let x = 0; x < canvas.width; x += 50) {
        ctx.fillText(`${x}`, x, 10)
      }
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      drawXrayGrid()
      drawScanLine()
      drawMarkers()

      particles.forEach((particle, i) => {
        particle.x += particle.speedX
        particle.y += particle.speedY

        if (particle.x < 0) particle.x = canvas.width
        if (particle.x > canvas.width) particle.x = 0
        if (particle.y < 0) particle.y = canvas.height
        if (particle.y > canvas.height) particle.y = 0

        ctx.beginPath()
        ctx.fillStyle = `rgba(44, 62, 80, ${particle.opacity})`
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2)
        ctx.fill()

        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j]
          const distance = Math.sqrt(Math.pow(particle.x - p2.x, 2) + Math.pow(particle.y - p2.y, 2))
          
          if (distance < 100) {
            ctx.beginPath()
            ctx.strokeStyle = `rgba(44, 62, 80, ${0.1 * (1 - distance / 100)})`
            ctx.lineWidth = 0.5
            ctx.moveTo(particle.x, particle.y)
            ctx.lineTo(p2.x, p2.y)
            ctx.stroke()
          }
        }
      })

      requestAnimationFrame(animate)
    }

    animate()

    return () => window.removeEventListener('resize', resizeCanvas)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      await signIn(formData.email, formData.password)
      navigate('/dashboard')
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao fazer login',
        description: 'Verifique suas credenciais e tente novamente.',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Gradientes animados em camadas */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent sm:animate-pulse-slow animate-pulse-slower" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10 sm:animate-gradient-slow animate-gradient-slower" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_50%,_rgba(0,0,0,0.1)_100%)]" />
      </div>

      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        style={{ opacity: 0.7 }}
      />

      <div className="relative container flex flex-col items-center min-h-screen px-4">
        <div className="mt-12">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-24 h-24 rounded-3xl flex items-center justify-center bg-primary shadow-xl backdrop-blur-sm bg-opacity-90 mb-6">
              <svg
                className="w-12 h-12 text-white"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 12h2l2 -8 2 16 2 -12 2 8 1.5 -3 2 0 1 -2 3.5 0" />
                <path d="M3 8h18" strokeOpacity="0.3" strokeWidth="1" />
                <path d="M3 16h18" strokeOpacity="0.3" strokeWidth="1" />
              </svg>
            </div>
            <span className="font-semibold text-4xl">Simples.ECG</span>
          </Link>
        </div>

        <div className="flex flex-col items-center justify-center flex-grow w-full max-w-sm">
          <div className="w-full space-y-8 backdrop-blur-sm bg-white/5 p-8 rounded-2xl border border-primary/10 shadow-2xl">
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-primary">Bem-vindo de volta</h2>
              <p className="text-sm text-muted-foreground">
                Entre para acessar sua conta
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="bg-white/10 border-primary/20 focus:border-primary/40 transition-colors"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="bg-white/10 border-primary/20 focus:border-primary/40 transition-colors"
                  required
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-14 text-lg font-semibold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 bg-gradient-to-r from-primary/90 via-primary to-primary-200 hover:scale-[1.02] hover:brightness-110"
                disabled={loading}
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground">
              Não tem uma conta?{' '}
              <Link to="/register" className="font-medium text-primary hover:text-primary/80 transition-colors hover:underline">
                Registre-se
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
} 